<template>
    <form class="booking-quote-review" :class="{initializing: !initialized}" @submit.prevent="submit">
      <div v-if="!data.initiatePayment">
        <header class="booking-quote-review-property">
          <div v-if="typeof data.listing.media !== 'undefined' && data.listing.media.length">
            <img :src="data.listing.media[0].urls.thumbnail_large" :alt="data.listing.media[0].name" class="cover">
          </div>
          <div>
            <h4 class="font-md wt-md mb-2">{{ data.listing.space_name }}</h4>
            <h5 class="wt-std fg-primary mb-6">
              {{ data.listing.address_label }}
            </h5>
            <p class="lh-std">
              <span class="fg-primary">Thank you!</span> Your booking request has been approved.
              Please review and accept the quote below.
            </p>

          </div>
        </header>

        <template v-if="data.lister_requirement">
          <h5 class="modal-subheading">Lister's Requirement</h5>
          <div class="content fg-dark-red wt-bold">  {{ data.lister_requirement }} </div>
        </template>

        <h5 class="modal-subheading">Dates &amp; times</h5>

        <div class="content">
          <div v-for="(date, index) in data.days_and_times_formatted">
            <h3 class="fg-primary wt-bold mb-4">
              {{ date.day }}
            </h3>
            <div class="lh-std mb-2">
              <span class="wt-bold fg-mid-grey">
                {{ data.type === 'recce' ? 'Arrival time' : 'Call time' }}:
              </span>
              <span class="wt-std">
                {{ date.time }}
              </span>
            </div>
            <div class="lh-std" :class="{'mb-6': (index+1) !== data.days_and_times_formatted.length}">
              <span class="wt-bold fg-mid-grey">
                Address:
              </span>
              <span class="wt-std">
                Address is supplied after booking is completed.
              </span>
            </div>
            <hr v-if="(index+1) !== data.days_and_times_formatted.length" class="thin mb-6"/>
          </div>
        </div>
      </div>

      <div v-if="data.type === 'booking'">
        <h5 class="modal-subheading">Insurance</h5>

        <div class="content">
          <div class="form-group vanilla double" :class="{'has-input-error': errors.insurance_documents}">
            <label :for="`attach-signed-schedule_${this._uid}`">
              Please ensure current proof of insurance is uploaded
            </label>
            <attachment-uploader
                :id="`attach-signed-schedule_${this._uid}`"
                name="insurance_documents"
                :multiple="true"
                :max-items="6"
                :initial-value="insurance_documents"
                v-model="insurance_documents"
            ></attachment-uploader>
            <input-error v-if="errors.insurance_documents">
              {{ errors.insurance_documents[0] }}
            </input-error>
          </div>
        </div>
      </div>

      <div v-if="data.has_cost &&  !data.initiatePayment">
        <h5 class="modal-subheading">Fees &amp; charges</h5>

        <div class="content">
          <table class="costs-table">
            <tr
                v-for="item in data.current_quote_for_user.user_line_items"
                :class="{'no-cost': !item.total}"
            >
              <td class="item">
                {{ item.label }}<span v-if="item.description"> - {{ item.description }}</span>
              </td>
              <td class="quantity">
                &nbsp;
              </td>
              <td class="cost">
                ${{ item.total_formatted }}
              </td>
            </tr>
            <tr class="subtotal">
              <td class="item" colspan="2">Sub total</td>
              <td class="cost">
                ${{ data.current_quote_for_user.sub_total_formatted }}
              </td>
            </tr>
            <tr class="subtotal">
              <td class="item" colspan="2">GST</td>
              <td class="cost">
                ${{ data.current_quote_for_user.gst_total_formatted }}
              </td>
            </tr>
            <tr class="subtotal">
              <td class="item" colspan="2">Total {{ data.booking_type_label }}</td>
              <td class="cost">
                ${{ data.current_quote_for_user.total_formatted }}
              </td>
            </tr>
            <tr class="grand-total">
              <td class="item" colspan="2">Total Payment</td>
              <td class="cost">
                ${{ data.current_quote_for_user.grand_total_formatted }}
              </td>
            </tr>
          </table>
          <p class="fg-dark-red wt-bold lh-loose align-center mb-6">
            Note: Overtime will be charged as per the
            <a href="/terms-and-conditions/hirer" @click.stop.prevent="showHirerTermsModal" class="fg-brand-blue">hirer
              terms &amp; conditions</a>.
          </p>
        </div>
      </div>


    <div v-if="!data.initiatePayment" class="form-group vanilla checkbox-container no-divider mb-6"
      :class="{'has-input-error': errors.agree_terms}">
      <checkbox v-model="agree_terms" :required="true">
        <template v-slot:checkbox-label>
          I agree to the hirer
          <a href="/terms-and-conditions/hirer" @click.stop.prevent="showHirerTermsModal">terms &amp; conditions</a>
          <span class="required">*</span>
        </template>
      </checkbox>
     <input-error v-if="errors.agree_terms">
      {{ errors.agree_terms[0] }}
    </input-error>
    </div>

      <div v-if="data.has_cost && data.initiatePayment">
        <div class="form-section booking-quote-review-payment">
          <div class="columned two-column">
            <div class="form-group vanilla">
              <stripe-select-card
                  @loadingStateChange="state => this.loading = state"
                  ref="selectCard"
                  @input="setPaymentMethod"
              ></stripe-select-card>
            </div>
            <div class="form-group vanilla">
              <button type="button" class="button muted md" title="Add a new card" @click="showAddPaymentModal">
                <svg-icon id="visa"></svg-icon>
                <span>Add a new card</span>
              </button>
            </div>
          </div>
        </div>

        <p class="lh-loose font-sm my-6" v-if="data.has_cost">
          <template v-if="data.type === 'recce'">
            100% payment required at the time of booking.
          </template>
          <template v-else>
            Bookings are charged 50% at the time of booking, with the remainder 50% charged 3 days prior to booking date.
            100% payment is charged if booking is made within 7 days of booking date.
          </template>
          Cancellation fees apply for last minute cancellations within 72 hours, however bookings can be paused and
          dates rescheduled for no fee due to unforeseen circumstances.
          Please see <a href="/terms-and-conditions/hirer" class="fg-brand-blue"
                        @click.stop.prevent="showHirerTermsModal">Hirer Terms &amp; Conditions</a>
          for cancellation.
        </p>
      </div>


      <div class="content" v-if="(data.is_payable || !data.has_cost) && data.initiatePayment">
        The fee for your Recce request has been waived but please contact us if you need to cancel or reschedule.
      </div>


      <div class="mt-6 align-right" v-if="(data.is_payable || !data.has_cost) && !data.initiatePayment">
        <loader-button
            type="submit"
            :loading="loading"
            :label="buttonLabel"
            class="button primary success"
        ></loader-button>
      </div>




      <div class="mt-6 align-right" v-if="(data.is_payable || !data.has_cost) && data.initiatePayment">
        <loader-button
            type="button"
            :loading="loading"
            :label="buttonLabel"
            @click.native="pay"
            class="button primary success"
        ></loader-button>
      </div>

    </form>
  </template>

  <script>
  import BaseModal from '../../Base'
  import Vue from "vue";
  import { StripePlugin } from "@vue-stripe/vue-stripe";

  const options = {
    pk: process.env.MIX_STRIPE_PUBLISHABLE,
    locale: "auto",
  };

  Vue.use(StripePlugin, options);

  export default {
    extends: BaseModal,
    props: {
      data: {
        type: Object,
        required: false
      }
    },
    data () {
      return {
        loading: false,
        errors: {},
        activeSection: 'dates-and-times',
        initialized: true,
        insurance_documents: this.$store.getters['user/insuranceDocuments'],
        payment_method: null,
        agree_terms: false,
        initiatePayment: false,
      }
    },
    methods: {
      submit () {
        this.errors = {}
        this.loading = true
        axios.put(`/api/account/bookings/${this.data.id}/hirerquoteconfirm`, {
          insurance_documents: this.insurance_documents,
          // payment_method: this.payment_method,
          agree_terms: this.agree_terms
        })
            .then(() => {
              // this.$emit('confirm')
              this.$eventBus.$emit('update_listing_booking-item')
              this.data.initiatePayment = true;
            })
            .catch(error => {
              if (error.response.status === 422) {
                this.$toasted
                    .show(`Your ${this.data.booking_type_label} could not be confirmed, please review errors and try again.`, {
                      type: 'error'
                    })
                return this.errors = error.response.data.errors
              }

              if (error.response.status === 409) {
                return this.$toasted.show(error.response.data, { type: 'error' })
              }

              this.$toasted.show(
                  `Your ${this.data.booking_type_label} could not be confirmed at this time, please refresh the page and try again.`, {
                    type: 'error'
                  })
            })
            .then(() => {
              this.loading = false
            })
      },

      handleConfirmCardPaymentFor3DS(response)
      {
        const {paymentIntent, error} = response;

        if (error) {
          this.$toasted
            .show(`There was an error while confirming your payment. Please contact your bank or add a new card.`, {
              type: 'error'
            })
          return this.errors = error;
        }

        axios.put(`/api/account/bookings/${this.data.id}/verifypayment`, {
          payment_intent_id: paymentIntent.id,
          payment_method: this.payment_method,
        })
        .then((res) => {
            this.$emit('confirm')
            this.$eventBus.$emit('update_listing_booking-item')
            this.$store.dispatch('modals/show', {
              title: `Thanks ${this.data.hirer_name}`,
              type: 'booking-confirmation',
              narrow: true,
              data: this.data
            })
                .then(() => {})
                .catch(() => {})
        })
        .catch(error => {
              if (error.response.status === 422) {
                this.$toasted
                    .show(`Your ${this.data.booking_type_label} your payment could not be confirmed`, {
                      type: 'error'
                    })
                return this.errors = error.response.data.errors
              }

              if (error.response.status === 409) {
                return this.$toasted.show(error.response.data, { type: 'error' })
              }

              this.$toasted.show(
                  `Your ${this.data.booking_type_label} could not be confirmed at this time, please refresh the page and try again.`, {
                    type: 'error'
                  })
            })

            .then(() => {
              this.loading = false
            })



      },

      pay () {
        this.errors = {}
        this.loading = true
        axios.put(`/api/account/bookings/${this.data.id}/pay`, {
          insurance_documents: this.insurance_documents,
          payment_method: this.payment_method,
          // agree_terms: this.agree_terms
        })
            .then((res) => {
              let stripeActionRequired = false;
              if (res.data.requires_action !== undefined && res.data.requires_action){
                stripeActionRequired = true;
                this.$stripe.confirmCardPayment(res.data.payment_intent_client_secret).then((res) =>{
                  this.handleConfirmCardPaymentFor3DS(res);
                });;
              }

              if (stripeActionRequired) {
                return;
              }
              this.$emit('confirm')
              this.$eventBus.$emit('update_listing_booking-item')
              this.$store.dispatch('modals/show', {
                title: `Thanks ${this.data.hirer_name}`,
                type: 'booking-confirmation',
                narrow: true,
                data: this.data
              })
                  .then(() => {})
                  .catch(() => {})
            })
            .catch(error => {
              if (error.response.status === 422) {
                this.$toasted
                    .show(`Your ${this.data.booking_type_label} could not be confirmed, please review errors and try again.`, {
                      type: 'error'
                    })
                return this.errors = error.response.data.errors
              }

              if (error.response.status === 409) {
                return this.$toasted.show(error.response.data, { type: 'error' })
              }

              this.$toasted.show(
                  `Your ${this.data.booking_type_label} could not be confirmed at this time, please refresh the page and try again.`, {
                    type: 'error'
                  })
            })
            .then(() => {
              this.loading = false
            })
      },

      setPaymentMethod (paymentMethod) {
        this.payment_method = paymentMethod.id
      },
      showAddPaymentModal () {
        this.$store.dispatch('modals/show', {
          title: 'Add a new card', type: 'booking-add-card', mini: true
        })
            .then(response => {
              this.$refs.selectCard.loadPaymentMethods()
                  .then(() => {
                    if (typeof response.related.id !== 'undefined') {
                      this.$refs.selectCard.selectCardById(response.related.id)
                    }
                  })
            })
            .catch(() => {})
      },
      showHirerTermsModal () {
        window.open("/pdf/hirer-agreement.pdf", '_blank');
      },
    },
    computed: {

      buttonLabel () {
        if (this.data.has_cost) {
          return  this.data.initiatePayment ? 'Pay Now '  + this.data.current_quote_for_user.grand_total_formatted : 'Confirm & proceed';
        }

        return  this.data.initiatePayment ? 'Pay Now'  + this.data.current_quote_for_user.grand_total_formatted : 'Confirm & proceed'
      }
    }
  }
  </script>
